import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Row, Col, Image, Typography } from 'antd';

const { Text } = Typography;

interface OrdersDrawerProps {
  visible: boolean;
  order: any;
  setVisible: (visible: boolean) => void;
  backToProgrammed: (orderdId: string, lineItems: Array<object>) => void;
}

const flattenLineItems = (lineItems: any[]) => {
  return lineItems?.flatMap((item) => [
    // Flattening product items
    ...Array(item.qty).fill({
      productId: item?.product?.product?._id,
      name: item?.product?.product?.name + (item?.product?.title ? ` (${item?.product?.title})` : ""),
      image: item?.product?.img,
    }),
    // Flattening complement items
    ...item.complements?.flatMap((complement) =>
      Array(complement.quantity).fill({
        productId: complement?._id,
        name: `${complement.variant?.title ? complement.variant?.title : complement?.variant?.product?.name} (Complemento)`,
        image: complement.variant?.img,
      })) || [],
    // Flattening beverage items
    ...item.beverages?.flatMap((beverage) =>
      Array(beverage.quantity).fill({
        productId: beverage?._id,
        name: `${beverage.variant?.title ? beverage.variant?.title : beverage.variant?.product?.name} (Bebida)`,
        image: beverage.variant?.img,
      })) || [],
    // Flattening extra items
    ...item.extras?.flatMap((extra) =>
      Array(extra.quantity).fill({
        productId: extra?._id,
        name: `${extra.variant?.title ? extra.variant?.title : extra.variant?.product?.name} (Extra)`,
        image: extra.variant?.img,
      })) || [],
  ]);
};

const OrdersDrawer: React.FC<OrdersDrawerProps> = ({ visible, setVisible, order, backToProgrammed }) => {
  const [selectedItems, setSelectedItems] = useState<Map<string, number>>(new Map());
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);

  const productList = flattenLineItems(order?.lineItems || []);

  useEffect(() => {
    if (!visible) {
      setSelectedItems(new Map());
      setSelectedIndexes([]);
    }
  }, [visible]);

  const toggleItem = (productId: string, isSelected: boolean) => {
    setSelectedItems((prev) => {
      const updatedMap = new Map(prev);
      const currentQty = updatedMap.get(productId) || 0;

      if (isSelected) {
        updatedMap.set(productId, currentQty + 1);
      } else {
        if (currentQty > 1) {
          updatedMap.set(productId, currentQty - 1);
        } else {
          updatedMap.delete(productId);
        }
      }

      return updatedMap;
    });
  };

  const isChecked = (index: number) => {
    return selectedIndexes.includes(index);
  };

  const onClose = () => {
    setSelectedItems(new Map());
    setSelectedIndexes([]);
    setVisible(false);
  };

  const handleSendBack = () => {
    const itemsToSendBack = Array.from(selectedItems.entries()).map(([productId, qty]) => ({
      variant: productId,
      qty,
    }));

    backToProgrammed(order?._id, itemsToSendBack);
    onClose();
  };

  return (
    <Modal
      title={`Order ${order?.reference}`}
      width={800}
      onCancel={onClose}
      open={visible}
      onOk={handleSendBack}
      okButtonProps={{ disabled: selectedItems.size === 0 }}
    >
      <Row gutter={[16, 16]}>
        {productList.map((item, index) => (
          <Col span={24} key={`${item.name}-${index}`}>
            <Checkbox
              checked={isChecked(index)} // Check if the index is selected
              onChange={(e) => {
                const isSelected = e.target.checked;
                toggleItem(item.productId, isSelected);
                setSelectedIndexes((prev) => {
                  const newIndexes = [...prev];
                  if (isSelected) {
                    newIndexes.push(index); // Add index if selected
                  } else {
                    newIndexes.splice(newIndexes.indexOf(index), 1); // Remove index if deselected
                  }
                  return newIndexes;
                });
              }}

            >
              <div style={{ display: 'flex',
                alignItems: 'center',
                gap: '12px' }}>
                {item.image && (
                  <Image
                    src={item.image}
                    width={40}
                    height={40}
                    style={{ objectFit: 'cover',
                      borderRadius: '6px' }}
                    preview={false}
                  />
                )}
                <Text>{item.name}</Text>
              </div>
            </Checkbox>
          </Col>
        ))}
      </Row>
    </Modal>
  );
};

export { OrdersDrawer };
